(() => {
  "use strict";

  // ---------------------------
  // Qty input
  // ---------------------------
  if (!customElements.get("pdp-qty-input")) {
    customElements.define(
      "pdp-qty-input",
      class extends HTMLElement {
        constructor() {
          super();
          this.qtyInput = this.querySelector('input[data-input="qty"]');
          this.qtyAdjustDecrease = this.querySelector("button#qtyDown");
          this.qtyAdjustIncrease = this.querySelector("button#qtyUp");

          this.minLimit = parseInt(this.dataset.minCount, 10);
          this.maxLimit = parseInt(this.dataset.maxCount, 10);
          this.increment = this.dataset.increment ? parseInt(this.dataset.increment, 10) : 1;

          this.qtyAdjustIncrease?.addEventListener("click", () => this.updateInput("up"));
          this.qtyAdjustDecrease?.addEventListener("click", () => this.updateInput("down"));
          this.refresh();
        }

        refresh() {
          const v = parseInt(this.qtyInput?.value || "1", 10);
          const nextUp = v + this.increment;
          const nextDown = v - this.increment;

          if (this.qtyAdjustIncrease) this.qtyAdjustIncrease.disabled = nextUp > this.maxLimit;
          if (this.qtyAdjustDecrease) this.qtyAdjustDecrease.disabled = nextDown < this.minLimit;
        }

        updateInput(dir) {
          const current = parseInt(this.qtyInput?.value || "1", 10);
          const next = dir === "up" ? current + this.increment : current - this.increment;
          if (this.qtyInput) this.qtyInput.value = String(next);
          this.refresh();
        }
      }
    );
  }

  // ---------------------------
  // Main PDP
  // ---------------------------
  if (!customElements.get("main-pdp")) {
    customElements.define(
      "main-pdp",
      class extends HTMLElement {
        constructor() {
          super();

          this._io = null;
          this._idFieldObserver = null;

          this.productJson = this.readProductJson();

          this.init();

          document.addEventListener("shopify:section:load", (e) => {
            const id = this.dataset.productUpdate;
            if (e.detail.sectionId === id) this.reinit();
          });

          this.addEventListener("section:updated", () => this.reinit());
        }

        disconnectedCallback() {
          if (this._io) this._io.disconnect();
          if (this._idFieldObserver) this._idFieldObserver.disconnect();
        }

        reinit() {
          this.productJson = this.readProductJson();
          this.init();
        }

        init() {
          const margin =
            parseInt(getComputedStyle(document.documentElement).getPropertyValue("--site-margin"), 10) || 0;
          const gutters =
            parseInt(getComputedStyle(document.documentElement).getPropertyValue("--site-gutters"), 10) || 0;
          this.scrollOffset = 48 + margin + gutters;

          this.setupThumbnailClicks();
          this.setupSlideObserver();

          this.setupVariantWatchers();

          // initial sync + render
          this.syncFeaturedMediaFromVariantId();
          this.applyMediaVisibility();
        }

        // ---------------------------
        // Product JSON
        // ---------------------------
        readProductJson() {
          const node = this.querySelector("[data-pdp-product-json]");
          if (!node) return null;
          try {
            return JSON.parse(node.textContent || "{}");
          } catch {
            return null;
          }
        }

        findVariantById(variantId) {
          const pid = String(variantId || "");
          const variants = this.productJson?.variants || [];
          return variants.find((v) => String(v.id) === pid) || null;
        }

        syncFeaturedMediaFromVariantId() {
          const vid = String(this.dataset.currentVariantId || "");
          if (!vid || !this.productJson) return;

          const v = this.findVariantById(vid);
          const featuredId = v?.featured_media?.id ? String(v.featured_media.id) : "";

          if (featuredId) this.dataset.currentFeaturedMediaId = featuredId;
        }

        // ---------------------------
        // Thumbnails click
        // ---------------------------
        setupThumbnailClicks() {
          this.desktopThumbs = Array.from(
            this.querySelectorAll(".thumbnails:not(.pdp-slider-nav) button[data-index]")
          );

          this.desktopThumbs.forEach((btn) => {
            btn.addEventListener("click", (e) => {
              const idx = e.currentTarget?.dataset?.index;
              const slide = this.querySelector(`.slider-slide[data-index="${idx}"]`);
              if (!slide) return;

              const top = slide.offsetTop - this.scrollOffset;
              window.scrollTo({ top, behavior: "smooth" });
              this.setActiveThumb(idx);
            });
          });
        }

        // ---------------------------
        // Slide observer -> highlight
        // ---------------------------
        setupSlideObserver() {
          if (this._io) this._io.disconnect();

          const slides = Array.from(this.querySelectorAll(".slider-slide[data-index]"));
          this._io = new IntersectionObserver(
            (entries) => {
              for (const entry of entries) {
                if (!entry.isIntersecting) continue;
                if (entry.target.hasAttribute("hidden")) continue;

                const idx = entry.target.dataset.index;
                this.setActiveThumb(idx);
              }
            },
            { rootMargin: "-50% 0px -50% 0px", threshold: 0 }
          );

          slides.forEach((s) => this._io.observe(s));
        }

        setActiveThumb(index) {
          const thumbs = Array.from(
            this.querySelectorAll(".thumbnails:not(.pdp-slider-nav) button[data-index]")
          );

          thumbs.forEach((b) => b.style.setProperty("--highlight-opacity", 0));

          const active = this.querySelector(`.thumbnails:not(.pdp-slider-nav) button[data-index="${index}"]`);
          if (active && !this.isEffectivelyHidden(active)) {
            active.style.setProperty("--highlight-opacity", 1);
          }
        }

        // ---------------------------
        // Variant watchers (field OR URL)
        // ---------------------------
        setupVariantWatchers() {
          const root = this;

          const setVariantId = (variantId) => {
            if (!variantId) return;

            root.dataset.currentVariantId = String(variantId);
            root.syncFeaturedMediaFromVariantId();
            root.applyMediaVisibility();
          };

          // initial from URL if present
          const urlVid = new URL(location.href).searchParams.get("variant");
          if (urlVid) setVariantId(urlVid);

          // bind to id field if/when it appears (select or input)
          const findIdField = () => root.querySelector('[name="id"]');

          const bindToField = (field) => {
            const read = () => field.value;
            setVariantId(read());
            field.addEventListener("change", () => setVariantId(read()));
            field.addEventListener("input", () => setVariantId(read()));
          };

          const fieldNow = findIdField();
          if (fieldNow) {
            bindToField(fieldNow);
          } else {
            if (this._idFieldObserver) this._idFieldObserver.disconnect();
            this._idFieldObserver = new MutationObserver(() => {
              const f = findIdField();
              if (!f) return;
              this._idFieldObserver.disconnect();
              bindToField(f);
            });
            this._idFieldObserver.observe(this, { childList: true, subtree: true });
          }

          // URL change listeners
          window.addEventListener("popstate", () => {
            const v = new URL(location.href).searchParams.get("variant");
            if (v) setVariantId(v);
          });

          if (!window.__pdpVariantUrlHooked) {
            window.__pdpVariantUrlHooked = true;

            const wrap = (fn) =>
              function () {
                const ret = fn.apply(this, arguments);
                window.dispatchEvent(new Event("pdp:urlchange"));
                return ret;
              };

            history.pushState = wrap(history.pushState);
            history.replaceState = wrap(history.replaceState);
          }

          window.addEventListener("pdp:urlchange", () => {
            const v = new URL(location.href).searchParams.get("variant");
            if (v) setVariantId(v);
          });
        }

        // ---------------------------
        // Core behavior
        // ---------------------------
        applyMediaVisibility() {
          const toggleOn = String(this.dataset.hideVariantsMedia) === "true";
          const featuredId = String(this.dataset.currentFeaturedMediaId || "");

          // Always: pin featured to top so main image changes without scrolling
          if (featuredId) this.pinFeaturedMedia(featuredId);

          // If toggle OFF, show everything (but featured stays pinned)
          if (!toggleOn || !featuredId) {
            this.showAllMedia();
            this.updateThumbRailsVisibility();
            return;
          }

          // Toggle ON: show only featured
          const slides = Array.from(this.querySelectorAll(".slider-slide[data-media-id]"));
          const thumbs = Array.from(this.querySelectorAll("button[data-media-id]"));

          let featuredSlideFound = false;

          slides.forEach((slide) => {
            const mid = String(slide.dataset.mediaId || "");
            const show = mid === featuredId;
            if (show) featuredSlideFound = true;

            if (show) {
              slide.removeAttribute("hidden");
              slide.style.removeProperty("display");
            } else {
              slide.setAttribute("hidden", "hidden");
              slide.style.setProperty("display", "none", "important");
            }
          });

          // If we can't find a matching slide, fail open (prevents blank page)
          if (!featuredSlideFound) {
            this.showAllMedia();
            this.updateThumbRailsVisibility();
            return;
          }

          thumbs.forEach((btn) => {
            const mid = String(btn.dataset.mediaId || "");
            const show = mid === featuredId;

            if (show) {
              btn.removeAttribute("hidden");
              btn.style.removeProperty("display");
            } else {
              btn.setAttribute("hidden", "hidden");
              btn.style.setProperty("display", "none", "important");
            }
          });

          // highlight visible thumb (desktop)
          const visibleDesktopThumb = this.querySelector(
            `.thumbnails:not(.pdp-slider-nav) button[data-media-id="${CSS.escape(featuredId)}"]`
          );
          if (visibleDesktopThumb) this.setActiveThumb(visibleDesktopThumb.dataset.index);

          this.updateThumbRailsVisibility();
        }

        // Move featured slide + thumbs to the top of their rails (no scroll needed)
        pinFeaturedMedia(featuredId) {
          const sliderContainer = this.querySelector(".slider-container");
          if (!sliderContainer) return;

          const featuredSlide = sliderContainer.querySelector(
            `.slider-slide[data-media-id="${CSS.escape(featuredId)}"]`
          );
          if (featuredSlide && sliderContainer.firstElementChild !== featuredSlide) {
            sliderContainer.prepend(featuredSlide);
          }

          // desktop rail: sticky container holds buttons
          const desktopRail = this.querySelector(".thumbnails.md\\:block .sticky");
          if (desktopRail) {
            const btn = desktopRail.querySelector(`button[data-media-id="${CSS.escape(featuredId)}"]`);
            if (btn && desktopRail.firstElementChild !== btn) desktopRail.prepend(btn);
          }

          // mobile rail
          const mobileRail = this.querySelector(".pdp-slider-nav.thumbnails");
          if (mobileRail) {
            const btn = mobileRail.querySelector(`button[data-media-id="${CSS.escape(featuredId)}"]`);
            if (btn && mobileRail.firstElementChild !== btn) mobileRail.prepend(btn);
          }
        }

        showAllMedia() {
          const slides = Array.from(this.querySelectorAll(".slider-slide"));
          slides.forEach((s) => {
            s.removeAttribute("hidden");
            s.style.removeProperty("display");
          });

          const thumbs = Array.from(this.querySelectorAll("button[data-media-id]"));
          thumbs.forEach((b) => {
            b.removeAttribute("hidden");
            b.style.removeProperty("display");
          });
        }

        updateThumbRailsVisibility() {
          const visibleThumbs = Array.from(this.querySelectorAll("button[data-media-id]")).filter(
            (b) => !this.isEffectivelyHidden(b)
          );

          const desktopRail = this.querySelector(".thumbnails:not(.pdp-slider-nav)");
          const mobileRail = this.querySelector(".pdp-slider-nav.thumbnails");
          const showRails = visibleThumbs.length > 1;

          if (desktopRail) desktopRail.style.setProperty("display", showRails ? "" : "none", "important");
          if (mobileRail) mobileRail.style.setProperty("display", showRails ? "" : "none", "important");
        }

        isEffectivelyHidden(el) {
          if (!el) return true;
          if (el.hasAttribute("hidden")) return true;
          const cs = window.getComputedStyle(el);
          return cs.display === "none" || cs.visibility === "hidden" || cs.opacity === "0";
        }
      }
    );
  }
})();